import {forwardRef, useContext, useEffect, useRef, useState} from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import {getCdnUrl} from "../hooks/cdn-hook";
import {WebsiteContext} from "../index";
import useIsomorphicLayoutEffect from "../hooks/use-isomorphic-layout-effect";

export const Image = forwardRef(function Image(
    {
        id,
        imageId,
        imageWidth,
        imageHeight,
        lazy,
        src,
        pictureClassName,
        className,
        quality,
        responsiveWidths,
        width,
        height,
        alt,
        layout,
        style,
        onClick,
        cdnUrl,
        rootMargin = "0px",
        forceLoad = false,
        disableLoadAnimation = false
    },
    ref
) {
    const internalRef = useRef();
    const [visible, setVisible] = useState(false);
    const [hadError, setHadError] = useState(false);
    const [isLoaded, setIsLoaded] = useState(true);
    const context = useContext(WebsiteContext);

    const PLACEHOLDER = "https://dr1k2g3wmnols.cloudfront.net/images/image-placeholder.png";
    const TRANSPARENT_PIXEL = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

    const requestedSrc =
        src ||
        "https://dr1k2g3wmnols.cloudfront.net/images/image-placeholder.png";

    // If we've seen a load/decode error, force the placeholder and skip <picture> sources
    const effectiveSrc = hadError ? PLACEHOLDER : requestedSrc;

    // Format resolution: WebP > PNG > fallback to original (based on the *current* effective source)
    const isPngSource = effectiveSrc.endsWith(".png");
    const isSvgSource = effectiveSrc.endsWith(".svg");
    const fallbackFormat = isPngSource ? "png" : "jpg";

    // Combine internal and forwarded refs
    const combinedRef = (node) => {
        internalRef.current = node;
        if (typeof ref === "function") {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    // Synchronously check if the image is in view on mount
    useIsomorphicLayoutEffect(() => {
        if (internalRef.current) {
            const rect = internalRef.current.getBoundingClientRect();
            const isWithinViewport =
                rect.top < window.innerHeight &&
                rect.bottom > 0 &&
                rect.left < window.innerWidth &&
                rect.right > 0;

            if (isWithinViewport) {
                setVisible(true);
            }
        }
    }, []);

    // Use IntersectionObserver to set visibility if image comes into view later
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            {
                threshold: context?.picker ? 0 : 0.1,
                rootMargin: rootMargin || "0px"
            }
        );

        if (internalRef.current) {
            observer.observe(internalRef.current);
        }

        return () => observer.disconnect();
    }, [rootMargin]);

    const isEager = lazy === "eager";
    const isVisible = visible || isEager;
    const imgClass = disableLoadAnimation
        ? classNames(className)
        : classNames(
            "transition-opacity duration-700 ease-out transform",
            isVisible && isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
            className
        );

    // Only load real image sources when visible to avoid hidden breakpoint variants downloading.
    const shouldLoad = forceLoad || isVisible || context?.picker;

    // Conditionally set the loading attribute based on visibility:
    // "eager" if visible on page load, otherwise "lazy".
    const loadingAttr = visible ? "eager" : "lazy";
    const responsiveImageWidths = {
        mobile: responsiveWidths?.mobile ?? 416,
        tablet: responsiveWidths?.tablet ?? 1350,
        desktop: responsiveWidths?.desktop ?? 1920
    };
    const getResponsiveCurrentWidth = () => {
        if (typeof window === "undefined") {
            return responsiveImageWidths.desktop;
        }

        const viewportWidth = window.innerWidth;
        const devicePixelRatio = window.devicePixelRatio || 1;
        const baseWidth = viewportWidth <= 640
            ? responsiveImageWidths.mobile
            : viewportWidth <= 1350
                ? responsiveImageWidths.tablet
                : responsiveImageWidths.desktop;

        return Math.round(baseWidth * (devicePixelRatio > 1 ? 2 : 1));
    };
    const responsiveCurrentWidth = getResponsiveCurrentWidth();

    // One-shot error handler to force the placeholder and prevent loops
    const handleError = (event) => {
        const target = event?.currentTarget;
        if (!target || target.src === PLACEHOLDER) {
            return;
        }

        const attachPlaceholderLoadHandler = () => {
            if (target.complete && target.naturalWidth > 0) {
                setIsLoaded(true);
            } else {
                const handlePlaceholderLoad = () => {
                    setIsLoaded(true);
                    target.removeEventListener("load", handlePlaceholderLoad);
                };
                target.addEventListener("load", handlePlaceholderLoad, {once: true});
            }
        };

        target.src = PLACEHOLDER;
        setHadError(true);
        setIsLoaded(false);
        attachPlaceholderLoadHandler();
    };

    const handleLoad = () => {
        setIsLoaded(true);
    };

    useEffect(() => {
        setHadError(false);
        setIsLoaded(false);
    }, [requestedSrc]);

    useEffect(() => {
        if (shouldLoad) {
            // Prevent a visible placeholder flash when the real image starts loading.
            setIsLoaded(false);
        }
    }, [shouldLoad]);

    useEffect(() => {
        const imgEl = internalRef.current;
        if (!imgEl || hadError) return;

        if (imgEl.complete && imgEl.naturalWidth === 0) {
            imgEl.src = PLACEHOLDER;
            setHadError(true);
            setIsLoaded(false);
        }
    }, [requestedSrc, hadError]);

    useEffect(() => {
        const imgEl = internalRef.current;
        if (!imgEl || hadError) return;

        if (imgEl.complete && imgEl.naturalWidth > 0) {
            setIsLoaded(true);
        }
    }, [requestedSrc, hadError]);

    const cdnImageUrl = (type, imageWidth, imageHeight) =>
        getCdnUrl(effectiveSrc, type, imageWidth, imageHeight, quality, cdnUrl, context);

    // If it's an SVG (current source), render a simple <img>. Attach onError for fallback.
    if (isSvgSource) {
        // When hadError is true, use the placeholder directly (no CDN transform) to avoid another failure path.
        return (
            <img
                onClick={onClick}
                onError={handleError}
                onLoad={handleLoad}
                id={imageId}
                ref={combinedRef}
                className={imgClass}
                width={imageWidth}
                height={imageHeight}
                loading={lazy ? lazy : loadingAttr}
                src={shouldLoad ? (hadError ? PLACEHOLDER : cdnImageUrl(null, null, null)) : TRANSPARENT_PIXEL}
                alt={alt ?? ""}
                style={style}
            />
        );
    }

    if (hadError) {
        return (
            <img
                onClick={onClick}
                onError={handleError}
                onLoad={handleLoad}
                id={imageId}
                ref={combinedRef}
                className={imgClass}
                width={imageWidth}
                height={imageHeight}
                loading={lazy ? lazy : loadingAttr}
                src={PLACEHOLDER}
                alt={alt ?? ""}
                style={style}
            />
        );
    }

    return (
        <picture id={id} className={pictureClassName} data-image="">
            {layout === "fixed" && shouldLoad && (
                <>
                    <source
                        type="image/webp"
                        srcSet={`
                            ${cdnImageUrl('webp', width, height)} 1x,
                            ${cdnImageUrl('webp', width * 1.75, height)} 1.75x,
                            ${cdnImageUrl('webp', width * 2, height)} 2x,
                            ${cdnImageUrl('webp', width * 3, height)} 3x
                        `}
                    />
                    <source
                        type={`image/${fallbackFormat}`}
                        srcSet={`
                            ${cdnImageUrl(fallbackFormat, width, height)} 1x,
                            ${cdnImageUrl(fallbackFormat, width * 1.75, height)} 1.75x,
                            ${cdnImageUrl(fallbackFormat, width * 2, height)} 2x,
                            ${cdnImageUrl(fallbackFormat, width * 3, height)} 3x
                        `}
                    />
                </>
            )}

            {layout === "responsive" && shouldLoad && (
                <>
                    <source
                        type="image/webp"
                        media="(max-width: 640px)"
                        srcSet={`
                            ${cdnImageUrl('webp', responsiveImageWidths.mobile, null)} 1x,
                            ${cdnImageUrl('webp', responsiveImageWidths.mobile * 2, null)} 2x
                        `}
                    />
                    <source
                        type={`image/${fallbackFormat}`}
                        media="(max-width: 640px)"
                        srcSet={`
                            ${cdnImageUrl(fallbackFormat, responsiveImageWidths.mobile, null)} 1x,
                            ${cdnImageUrl(fallbackFormat, responsiveImageWidths.mobile * 2, null)} 2x
                        `}
                    />
                    <source
                        type="image/webp"
                        media="(max-width: 1350px)"
                        srcSet={`
                            ${cdnImageUrl('webp', responsiveImageWidths.tablet, null)} 1x,
                            ${cdnImageUrl('webp', responsiveImageWidths.tablet * 2, null)} 2x
                        `}
                    />
                    <source
                        type={`image/${fallbackFormat}`}
                        media="(max-width: 1350px)"
                        srcSet={`
                            ${cdnImageUrl(fallbackFormat, responsiveImageWidths.tablet, null)} 1x,
                            ${cdnImageUrl(fallbackFormat, responsiveImageWidths.tablet * 2, null)} 2x
                        `}
                    />
                    <source
                        type="image/webp"
                        media="(min-width: 1351px)"
                        srcSet={`
                            ${cdnImageUrl('webp', responsiveImageWidths.desktop, null)} 1x,
                            ${cdnImageUrl('webp', responsiveImageWidths.desktop * 2, null)} 2x
                        `}
                    />
                    <source
                        type={`image/${fallbackFormat}`}
                        media="(min-width: 1351px)"
                        srcSet={`
                            ${cdnImageUrl(fallbackFormat, responsiveImageWidths.desktop, null)} 1x,
                            ${cdnImageUrl(fallbackFormat, responsiveImageWidths.desktop * 2, null)} 2x
                        `}
                    />
                </>
            )}

            <img
                onClick={onClick}
                onError={handleError}
                onLoad={handleLoad}
                id={imageId}
                ref={combinedRef}
                className={imgClass}
                width={imageWidth}
                height={imageHeight}
                loading={lazy ? lazy : loadingAttr}
                src={shouldLoad
                    ? cdnImageUrl(
                        layout === "responsive" ? "webp" : fallbackFormat,
                        layout === "responsive" ? responsiveCurrentWidth : null,
                        null
                    )
                    : TRANSPARENT_PIXEL}
                alt={alt ?? ""}
                style={style}
            />
        </picture>
    );
});

Image.propTypes = {
    id: PropTypes.string,
    src: PropTypes.string,
    style: PropTypes.object,
    onClick: PropTypes.func,
    pictureClassName: PropTypes.string,
    className: PropTypes.string,
    quality: PropTypes.number,
    responsiveWidths: PropTypes.shape({
        mobile: PropTypes.number,
        tablet: PropTypes.number,
        desktop: PropTypes.number,
    }),
    rootMargin: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    layout: PropTypes.oneOf(["fixed", "responsive"]),
    forceLoad: PropTypes.bool,
    disableLoadAnimation: PropTypes.bool,
};

Image.defaultProps = {
    quality: 75,
    layout: "fixed",
    width: 720
};
